import React, { ReactNode } from 'react';
import styles from '@danske/sapphire-css/components/avatar/avatar.module.css';
import clsx from 'clsx';
import {
  SapphireStyleProps,
  useSapphireStyleProps,
  useThemeCheck,
} from '../../utils';
import { Icon } from '../../Icon';
import { GlobalDomAttributes } from '../../utils/types';
import { filterDOMProps } from 'react-aria/filterDOMProps';

const getIconSize = (size?: AvatarProps['size']) => {
  switch (size) {
    case 'md':
      return 'md';
    case 'lg':
      return 'lg';
    default:
      return 'sm';
  }
};

export type AvatarProps = GlobalDomAttributes &
  SapphireStyleProps & {
    /**
     * @default 'md'
     */
    size?: 'sm' | 'md' | 'lg';

    /**
     * The decorative color of the avatar.
     * Defaults to a neutral color if none is provided.
     */
    color?:
      | 'neutral'
      | 1
      | 2
      | 3
      | 4
      | 5
      | 6
      | 7
      | 8
      | 9
      | 10
      | 11
      | 12
      | 13
      | 14
      | 15
      | 16;
  } & (
    | {
        monogram: ReactNode;
        icon?: never;
      }
    | {
        monogram?: never;
        icon: ReactNode;
      }
  );

/**
 * Displays a user representation as either a monogram or an icon.
 * Supports three sizes and a range of decorative colors.
 */
export const Avatar = ({
  monogram,
  icon,
  color,
  size = 'md',
  ...props
}: AvatarProps): React.ReactElement => {
  useThemeCheck();
  const { styleProps } = useSapphireStyleProps(props);

  return (
    <div
      {...styleProps}
      {...filterDOMProps(props, { global: true })}
      className={clsx(
        styleProps.className,
        styles['sapphire-avatar'],
        {
          [styles['sapphire-avatar--lg']]: size === 'lg',
          [styles['sapphire-avatar--sm']]: size === 'sm',
        },
        color &&
          color !== 'neutral' &&
          styles[`sapphire-avatar--decorative-${color}`]
      )}
    >
      {icon ? <Icon size={getIconSize(size)}>{icon}</Icon> : monogram}
    </div>
  );
};
